import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withKeys as _withKeys, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "policy-sections-page" };
const _hoisted_2 = { class: "alert-message" };
const _hoisted_3 = { class: "policy-sections-container" };
const _hoisted_4 = { class: "files-list-section" };
const _hoisted_5 = { class: "section-header" };
const _hoisted_6 = ["disabled"];
const _hoisted_7 = { class: "search-box" };
const _hoisted_8 = {
    key: 0,
    class: "loading"
};
const _hoisted_9 = {
    key: 1,
    class: "files-list"
};
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "file-name" };
const _hoisted_12 = {
    key: 2,
    class: "empty-state"
};
const _hoisted_13 = {
    key: 0,
    class: "editor-section"
};
const _hoisted_14 = { class: "section-header" };
const _hoisted_15 = {
    key: 0,
    class: "loading"
};
const _hoisted_16 = {
    key: 1,
    class: "error-message"
};
const _hoisted_17 = {
    key: 2,
    class: "editor-wrapper"
};
const _hoisted_18 = {
    key: 0,
    class: "unsaved-indicator"
};
const _hoisted_19 = {
    key: 1,
    class: "empty-state-section"
};
const _hoisted_20 = { class: "new-file-form" };
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { EditorView, basicSetup } from 'codemirror';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorState } from '@codemirror/state';
import { useAdmin } from '@/composables/useAdmin';
import MGButton from '@/components/elements/MGButton.vue';
import MGDialog from '@/components/elements/MGDialog.vue';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicySectionsPage',
    setup(__props) {
        const admin = useAdmin();
        const editorContainer = ref(null);
        const files = ref(null);
        const selectedFile = ref(null);
        const fileContent = ref('');
        const originalContent = ref('');
        const loading = ref(false);
        const isLoadingContent = ref(false);
        const saving = ref(false);
        const loadError = ref(null);
        const showAlert = ref(false);
        const alertMessage = ref('');
        const alertVariant = ref('success');
        const searchQuery = ref('');
        const editor = ref(null);
        const showUnsavedWarning = ref(false);
        const pendingFileSwitch = ref(null);
        const hasUnsavedChanges = ref(false);
        const showNewFileDialog = ref(false);
        const newFileName = ref('');
        const showDeleteConfirm = ref(false);
        const filteredFiles = computed(() => {
            if (!files.value)
                return null;
            const jsonFiles = files.value.filter((f) => f.fileName.toLowerCase().includes('.json'));
            if (!searchQuery.value)
                return jsonFiles;
            return jsonFiles.filter((f) => f.fileName.toLowerCase().includes(searchQuery.value.toLowerCase()));
        });
        const isValidFileName = computed(() => {
            const name = newFileName.value.trim();
            return name.length > 5 && name.toLowerCase().endsWith('.json');
        });
        function loadFiles() {
            loading.value = true;
            admin.getConfigFiles((data) => {
                files.value = data;
                loading.value = false;
            }, (error) => {
                showAlertMessage(error, 'error');
                loading.value = false;
            });
        }
        function createEditorInstance(content) {
            const container = editorContainer.value;
            if (!container) {
                console.error('Editor container not found');
                return;
            }
            if (editor.value) {
                editor.value.destroy();
                editor.value = null;
            }
            container.innerHTML = '';
            const updateListener = EditorView.updateListener.of((update) => {
                if (update.docChanged) {
                    const currentContent = update.state.doc.toString();
                    fileContent.value = currentContent;
                    hasUnsavedChanges.value = currentContent !== originalContent.value;
                }
            });
            editor.value = new EditorView({
                state: EditorState.create({
                    doc: content,
                    extensions: [
                        basicSetup,
                        json(),
                        oneDark,
                        updateListener,
                        EditorView.theme({
                            '&': { height: '100%' },
                            '.cm-scroller': { overflow: 'auto' },
                            '.cm-content': { fontFamily: 'monospace' }
                        })
                    ]
                }),
                parent: container
            });
        }
        async function selectFileFn(file) {
            if (hasUnsavedChanges.value) {
                pendingFileSwitch.value = file;
                showUnsavedWarning.value = true;
                return;
            }
            await loadFileContent(file);
        }
        async function loadFileContent(file) {
            selectedFile.value = file.fileName;
            loadError.value = null;
            hasUnsavedChanges.value = false;
            isLoadingContent.value = true;
            admin.getConfigFileContent(file.fileName, async (data) => {
                let content;
                if (data.content) {
                    content = atob(data.content);
                }
                else {
                    content = JSON.stringify(data, null, 2);
                }
                fileContent.value = content;
                originalContent.value = content;
                isLoadingContent.value = false;
                await nextTick();
                createEditorInstance(content);
            }, (error) => {
                loadError.value = error;
                isLoadingContent.value = false;
            });
        }
        function saveContent() {
            if (!selectedFile.value || !editor.value)
                return;
            try {
                const content = editor.value.state.doc.toString();
                JSON.parse(content);
                saving.value = true;
                const base64Content = btoa(content);
                admin.saveConfigFile(selectedFile.value, base64Content, () => {
                    originalContent.value = content;
                    hasUnsavedChanges.value = false;
                    showAlertMessage('File saved successfully', 'success');
                    saving.value = false;
                }, (error) => {
                    showAlertMessage(error, 'error');
                    saving.value = false;
                });
            }
            catch (error) {
                if (error instanceof SyntaxError) {
                    showAlertMessage('Invalid JSON format. Please fix the errors before saving.', 'error');
                }
                else {
                    showAlertMessage('Error saving file', 'error');
                }
            }
        }
        function handleUnsavedAction(action) {
            showUnsavedWarning.value = false;
            if (action === 'discard' && pendingFileSwitch.value) {
                hasUnsavedChanges.value = false;
                loadFileContent(pendingFileSwitch.value);
            }
            pendingFileSwitch.value = null;
        }
        function showAlertMessage(message, variant = 'success') {
            alertMessage.value = message;
            alertVariant.value = variant;
            showAlert.value = true;
            if (variant === 'success') {
                setTimeout(() => {
                    showAlert.value = false;
                }, 4000);
            }
        }
        function createNewFile() {
            if (!isValidFileName.value)
                return;
            const fileName = newFileName.value.trim();
            const initialContent = '{\n  \n}';
            const base64Content = btoa(initialContent);
            admin.saveConfigFile(fileName, base64Content, () => {
                showNewFileDialog.value = false;
                newFileName.value = '';
                showAlertMessage('Section created successfully', 'success');
                admin.getConfigFiles((data) => {
                    files.value = data;
                    const newFile = data.find((f) => f.fileName === fileName);
                    if (newFile) {
                        loadFileContent(newFile);
                    }
                }, (error) => {
                    showAlertMessage(error, 'error');
                });
            }, (error) => {
                showAlertMessage(error, 'error');
            });
        }
        function deleteFileFn() {
            if (!selectedFile.value)
                return;
            const fileName = selectedFile.value;
            admin.deleteConfigFile(fileName, () => {
                showDeleteConfirm.value = false;
                showAlertMessage('Section deleted successfully', 'success');
                if (editor.value) {
                    editor.value.destroy();
                    editor.value = null;
                }
                selectedFile.value = null;
                fileContent.value = '';
                originalContent.value = '';
                hasUnsavedChanges.value = false;
                loadFiles();
            }, (error) => {
                showDeleteConfirm.value = false;
                showAlertMessage(error, 'error');
            });
        }
        onMounted(() => {
            loadFiles();
        });
        onBeforeUnmount(() => {
            if (editor.value) {
                editor.value.destroy();
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[23] || (_cache[23] = _createElementVNode("div", { class: "page-header" }, [
                    _createElementVNode("h1", null, "Policy Sections Admin"),
                    _createElementVNode("p", { class: "subtitle" }, "Manage JSON configuration files")
                ], -1)),
                _createVNode(_Transition, { name: "fade" }, {
                    default: _withCtx(() => [
                        (showAlert.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["alert", `alert-${alertVariant.value}`])
                            }, [
                                _createElementVNode("span", _hoisted_2, _toDisplayString(alertMessage.value), 1),
                                _createElementVNode("button", {
                                    class: "alert-close",
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showAlert.value = false))
                                }, "✕")
                            ], 2))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _cache[12] || (_cache[12] = _createElementVNode("h2", null, "Config Files", -1)),
                            _createElementVNode("button", {
                                class: "action-btn secondary",
                                onClick: loadFiles,
                                disabled: loading.value
                            }, " 🔄 ", 8, _hoisted_6)
                        ]),
                        _createElementVNode("div", _hoisted_7, [
                            _withDirectives(_createElementVNode("input", {
                                type: "text",
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((searchQuery).value = $event)),
                                placeholder: "Search files...",
                                class: "search-input"
                            }, null, 512), [
                                [_vModelText, searchQuery.value]
                            ])
                        ]),
                        (loading.value && !files.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _cache[13] || (_cache[13] = [
                                _createElementVNode("div", { class: "spinner" }, null, -1),
                                _createElementVNode("span", null, "Loading files...", -1)
                            ])))
                            : (filteredFiles.value && filteredFiles.value.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredFiles.value, (file) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                            key: file.fileName,
                                            class: _normalizeClass(["file-item", { active: selectedFile.value === file.fileName }]),
                                            onClick: ($event) => (selectFileFn(file))
                                        }, [
                                            _createElementVNode("span", _hoisted_11, _toDisplayString(file.fileName), 1)
                                        ], 10, _hoisted_10));
                                    }), 128))
                                ]))
                                : (_openBlock(), _createElementBlock("div", _hoisted_12, _cache[14] || (_cache[14] = [
                                    _createElementVNode("p", null, "No config files found", -1)
                                ])))
                    ]),
                    (selectedFile.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                                _createElementVNode("h2", null, _toDisplayString(selectedFile.value), 1),
                                _createVNode(AdminHeaderActions, {
                                    loading: saving.value,
                                    "has-id": !!selectedFile.value,
                                    onSave: saveContent,
                                    onDelete: _cache[2] || (_cache[2] = ($event) => (showDeleteConfirm.value = true)),
                                    onNew: _cache[3] || (_cache[3] = ($event) => (showNewFileDialog.value = true))
                                }, null, 8, ["loading", "has-id"])
                            ]),
                            (isLoadingContent.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_15, _cache[15] || (_cache[15] = [
                                    _createElementVNode("div", { class: "spinner" }, null, -1),
                                    _createElementVNode("span", null, "Loading file content...", -1)
                                ])))
                                : (loadError.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(loadError.value), 1))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                        _createElementVNode("div", {
                                            class: "editor-container",
                                            ref_key: "editorContainer",
                                            ref: editorContainer
                                        }, null, 512),
                                        (hasUnsavedChanges.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, " Unsaved changes "))
                                            : _createCommentVNode("", true)
                                    ]))
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_19, _cache[16] || (_cache[16] = [
                            _createElementVNode("p", null, "Select a file to edit", -1)
                        ])))
                ]),
                _createVNode(MGDialog, {
                    modelValue: showUnsavedWarning.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((showUnsavedWarning).value = $event)),
                    title: "Unsaved Changes",
                    "max-width": "400px",
                    "content-height": "auto"
                }, {
                    content: _withCtx(() => _cache[17] || (_cache[17] = [
                        _createElementVNode("p", null, "You have unsaved changes. Are you sure you want to switch files?", -1),
                        _createElementVNode("p", { class: "text-muted" }, "Your changes will be lost if you proceed.", -1)
                    ])),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Discard Changes",
                            onClick: _cache[4] || (_cache[4] = ($event) => (handleUnsavedAction('discard'))),
                            stacked: true
                        }),
                        _createVNode(MGButton, {
                            text: "Stay on Current File",
                            outline: true,
                            onClick: _cache[5] || (_cache[5] = ($event) => (handleUnsavedAction('cancel'))),
                            stacked: true
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(MGDialog, {
                    modelValue: showNewFileDialog.value,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((showNewFileDialog).value = $event)),
                    title: "Create New Section",
                    "max-width": "400px",
                    "content-height": "auto"
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("div", _hoisted_20, [
                            _cache[18] || (_cache[18] = _createElementVNode("label", { for: "newFileName" }, "File Name", -1)),
                            _withDirectives(_createElementVNode("input", {
                                id: "newFileName",
                                type: "text",
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((newFileName).value = $event)),
                                placeholder: "e.g. my-section.json",
                                class: "form-input",
                                onKeyup: _withKeys(createNewFile, ["enter"])
                            }, null, 544), [
                                [_vModelText, newFileName.value]
                            ]),
                            _cache[19] || (_cache[19] = _createElementVNode("p", { class: "input-hint" }, "Must end with .json", -1))
                        ])
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Create",
                            onClick: createNewFile,
                            stacked: true,
                            disabled: !isValidFileName.value
                        }, null, 8, ["disabled"]),
                        _createVNode(MGButton, {
                            text: "Cancel",
                            outline: true,
                            onClick: _cache[8] || (_cache[8] = ($event) => (showNewFileDialog.value = false)),
                            stacked: true
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(MGDialog, {
                    modelValue: showDeleteConfirm.value,
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((showDeleteConfirm).value = $event)),
                    title: "Delete Section",
                    "max-width": "400px",
                    "content-height": "auto"
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("p", null, [
                            _cache[20] || (_cache[20] = _createTextVNode("Are you sure you want to delete ")),
                            _createElementVNode("strong", null, _toDisplayString(selectedFile.value), 1),
                            _cache[21] || (_cache[21] = _createTextVNode("?"))
                        ]),
                        _cache[22] || (_cache[22] = _createElementVNode("p", { class: "text-muted" }, "This action cannot be undone.", -1))
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Delete",
                            onClick: deleteFileFn,
                            stacked: true
                        }),
                        _createVNode(MGButton, {
                            text: "Cancel",
                            outline: true,
                            onClick: _cache[10] || (_cache[10] = ($event) => (showDeleteConfirm.value = false)),
                            stacked: true
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ]));
        };
    }
});
